/**
 * Stable per-device identity for self-registering with the licensing
 * server. Two paths:
 *
 *   - APK host: AndroidBridge.getDeviceId() returns a JSON envelope with
 *     a UUID persisted in SharedPreferences plus Android-side metadata.
 *   - Browser host (kiosk in plain Chrome, marketing demo, etc.):
 *     fall back to a UUID stored in localStorage. Same primary-key
 *     stability as the APK case; we just have fewer secondary fields
 *     to report.
 *
 * Either way, the renderer ends up with a `DeviceIdentity` it can POST
 * to /devices/register on boot.
 */

const STORAGE_KEY = 'mobilauncher.deviceId';

export interface DeviceIdentity {
  deviceId: string;
  androidId: string | null;
  deviceName: string | null;
  osVersion: string | null;
  /** Android security patch level (e.g. 2026-05-05), when the host reports it. */
  securityPatch: string | null;
  appVersion: string | null;
  /**
   * Device's own LAN IPv4 (e.g. 192.168.1.42), when the host can report it.
   * The APK reads it from NetworkInterface and includes it in the envelope;
   * browsers / WebViews can't, so it stays null there.
   */
  localIpv4: string | null;
}

interface DeviceBridgeGlobals {
  MobiLauncherBridge?: { getDeviceId?: () => string };
}

function bridge(): DeviceBridgeGlobals['MobiLauncherBridge'] | undefined {
  return (window as unknown as DeviceBridgeGlobals).MobiLauncherBridge;
}

/** Returns the device identity payload, generating + persisting one on first call. */
export function getDeviceIdentity(): DeviceIdentity {
  const b = bridge();
  if (typeof b?.getDeviceId === 'function') {
    try {
      const parsed = JSON.parse(b.getDeviceId()) as Partial<DeviceIdentity>;
      if (typeof parsed.deviceId === 'string' && parsed.deviceId.length > 0) {
        return {
          deviceId: parsed.deviceId,
          androidId: parsed.androidId ?? null,
          deviceName: parsed.deviceName ?? null,
          osVersion: parsed.osVersion ?? null,
          securityPatch: parsed.securityPatch ?? null,
          appVersion: parsed.appVersion ?? null,
          localIpv4: parsed.localIpv4 ?? null,
        };
      }
    } catch {
      // Bridge returned malformed JSON — fall through to browser path.
    }
  }
  return browserIdentity();
}

function browserIdentity(): DeviceIdentity {
  const id = stableBrowserId();
  const ua = parseUserAgent();
  return {
    deviceId: id,
    androidId: null,
    deviceName: ua.deviceName,
    osVersion: ua.osVersion,
    // Security patch level is an Android/native concept — browsers can't report it.
    securityPatch: null,
    // Browser builds don't have an APK versionName. Label it explicitly
    // as "Browser <browser> <major>" so the Devices panel doesn't show
    // an em-dash for the OS/App column on every browser kiosk.
    appVersion: ua.appVersion,
    // Browsers can't read the host's LAN IP reliably (WebRTC mDNS hides it).
    localIpv4: null,
  };
}

/**
 * Resolve a stable browser deviceId. Prefers a persisted random UUID. When
 * storage is unavailable (private mode / sandboxed WebView), falls back to a
 * device fingerprint so the id — and therefore the device's pilot cohort —
 * survives reboots instead of churning a fresh UUID (and a new Devices row)
 * every boot. Tradeoff: identical storage-blocked devices may share an id.
 */
function stableBrowserId(): string {
  try {
    const existing = window.localStorage.getItem(STORAGE_KEY);
    if (existing) return existing;
    const fresh = window.crypto.randomUUID();
    window.localStorage.setItem(STORAGE_KEY, fresh);
    return fresh;
  } catch {
    return `fp-${deviceFingerprint()}`;
  }
}

/** Stable-per-device hash of the signals available without storage. */
function deviceFingerprint(): string {
  const parts = [
    typeof navigator !== 'undefined' ? navigator.userAgent : '',
    typeof navigator !== 'undefined' ? navigator.language : '',
    typeof screen !== 'undefined' ? `${String(screen.width)}x${String(screen.height)}x${String(screen.colorDepth)}` : '',
    typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : '',
  ].join('|');
  // FNV-1a, 32-bit — small, synchronous, no crypto needed for a non-secret id.
  let h = 0x811c9dc5;
  for (let i = 0; i < parts.length; i++) {
    h ^= parts.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}

interface ParsedUserAgent {
  deviceName: string | null;
  osVersion: string | null;
  appVersion: string | null;
}

function parseUserAgent(): ParsedUserAgent {
  if (typeof navigator === 'undefined') {
    return { deviceName: null, osVersion: null, appVersion: null };
  }
  const ua = navigator.userAgent;
  // Order matters: Edg before Chrome (Edge UA also contains Chrome),
  // Safari only when Chrome isn't present (Chrome UA contains Safari).
  const browserMatch =
    /Edg\/([\d.]+)/.exec(ua) ??
    /Chrome\/([\d.]+)/.exec(ua) ??
    /Firefox\/([\d.]+)/.exec(ua) ??
    (/Safari\/([\d.]+)/.exec(ua) && !ua.includes('Chrome')
      ? /Version\/([\d.]+)/.exec(ua) ?? /Safari\/([\d.]+)/.exec(ua)
      : null);
  const browserName = ua.includes('Edg/')
    ? 'Edge'
    : ua.includes('Chrome/') && !ua.includes('Edg/')
      ? 'Chrome'
      : ua.includes('Firefox/')
        ? 'Firefox'
        : ua.includes('Safari/')
          ? 'Safari'
          : null;
  const browserVersion = browserMatch?.[1]?.split('.')[0] ?? null;

  const osMatch =
    /Windows NT ([\d.]+)/.exec(ua) ??
    /Mac OS X ([\d_.]+)/.exec(ua) ??
    /Android ([\d.]+)/.exec(ua) ??
    /CPU (?:iPhone )?OS ([\d_]+)/.exec(ua);
  const osLabel = ua.includes('Windows NT')
    ? `Windows ${osMatch?.[1] ?? ''}`.trim()
    : ua.includes('Mac OS X')
      ? `macOS ${(osMatch?.[1] ?? '').replace(/_/g, '.')}`.trim()
      : ua.includes('Android')
        ? `Android ${osMatch?.[1] ?? ''}`.trim()
        : /iPhone|iPad/.test(ua)
          ? `iOS ${(osMatch?.[1] ?? '').replace(/_/g, '.')}`.trim()
          : ua.includes('Linux')
            ? 'Linux'
            : null;

  const deviceName =
    browserName && browserVersion && osLabel
      ? `${browserName} ${browserVersion} on ${osLabel}`
      : ua.slice(0, 120);
  const appVersion =
    browserName && browserVersion ? `Browser • ${browserName} ${browserVersion}` : 'Browser';

  return {
    deviceName,
    osVersion: osLabel,
    appVersion,
  };
}

export interface RegisterDeviceOptions {
  /** Absolute base URL of the licensing server (e.g. https://api.mobilauncher.com). */
  apiBaseUrl: string;
  tenantId: string;
  identity?: DeviceIdentity;
  fetchImpl?: typeof fetch;
}

/**
 * Fire-and-forget device registration. Returns the deviceId on success
 * so callers can stash it on the engine + append it as `?deviceId=` on
 * subsequent config polls. Resolves to `null` on any failure (network,
 * 4xx, etc.) — registration is non-critical, the launcher must boot
 * regardless.
 */
export async function registerDevice(opts: RegisterDeviceOptions): Promise<string | null> {
  const identity = opts.identity ?? getDeviceIdentity();
  const url = `${opts.apiBaseUrl.replace(/\/$/, '')}/devices/register`;
  const f = opts.fetchImpl ?? fetch;
  try {
    const res = await f(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        deviceId: identity.deviceId,
        tenantId: opts.tenantId,
        androidId: identity.androidId,
        deviceName: identity.deviceName,
        osVersion: identity.osVersion,
        securityPatch: identity.securityPatch,
        appVersion: identity.appVersion,
        localIpv4: identity.localIpv4,
      }),
      keepalive: true,
    });
    if (!res.ok) return null;
    return identity.deviceId;
  } catch {
    return null;
  }
}

/**
 * Strip a configs/licenses URL down to its origin so we can build the
 * /devices/register URL on the same host. Works for both the hosted
 * `https://api.mobilauncher.com/configs/<id>` shape and an explicit
 * VITE_CONFIG_URL_TEMPLATE-overridden base.
 */
export function apiBaseUrlFromConfigUrl(configUrl: string): string | null {
  try {
    const u = new URL(configUrl, window.location.href);
    if (u.protocol === 'http:' || u.protocol === 'https:') return u.origin;
    return null;
  } catch {
    return null;
  }
}
